import React from 'react';
import { TENANT_STATUS_CONFIG } from './extended-mock-data.jsx';
import { Icon } from './icons.jsx';
import { uiStatusToCommercial } from './services.jsx';
// Tenant Status Components — Super Admin Tezus v2
// TenantStatusBadge (extended), TenantStatusSelect,
// TenantStatusTimeline, TenantStatusReasonModal
// Suporta ambos naming conventions: inglês (active/overdue) e português (ativo/inadimplente)

// ── Status Config ──────────────────────────────────────────────────────────────
const getStatusCfg = (status) => {
  const cfg = TENANT_STATUS_CONFIG || {};
  return cfg[status] || { label: status, color: 'var(--text-muted)', bg: 'var(--bg-subtle)', dot: 'var(--text-muted)' };
};

// ── TenantStatusBadge (sobrescreve a versão simples do cockpit) ───────────────
const TenantStatusBadge = ({ status, showIcon = false, size = 'md' }) => {
  const c = getStatusCfg(status);
  const pad = size === 'sm' ? '2px 8px' : size === 'lg' ? '5px 14px' : '3px 10px';
  const fs  = size === 'sm' ? 11 : size === 'lg' ? 13 : 12;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: pad, borderRadius: 20, background: c.bg, color: c.color, fontSize: fs, fontWeight: 700, whiteSpace: 'nowrap' }}>
      <span style={{ width: size === 'lg' ? 7 : 5, height: size === 'lg' ? 7 : 5, borderRadius: '50%', background: c.color, flexShrink: 0 }} />
      {c.label}
    </span>
  );
};

// ── BillingStatusBadge ─────────────────────────────────────────────────────────
const BillingStatusBadge = ({ status, label }) => {
  const cfg = {
    active:       { label: 'Ativo',         bg: '#e8f5ee', color: '#0f7a3e' },
    trial:        { label: 'Trial',         bg: '#dde7ff', color: '#1d4ed8' },
    trialing:     { label: 'Trial',         bg: '#dde7ff', color: '#1d4ed8' },
    past_due:     { label: 'Inadimplente',  bg: '#fef3c7', color: '#b45309' },
    overdue:      { label: 'Inadimplente',  bg: '#fef3c7', color: '#b45309' },
    unpaid:       { label: 'Inadimplente',  bg: '#fdecf0', color: '#EC2A58' },
    suspended:    { label: 'Suspenso',      bg: '#fdecf0', color: '#EC2A58' },
    cancelled:    { label: 'Cancelado',     bg: '#f4f5f7', color: '#6b7691' },
  };
  const c = cfg[status] || cfg.cancelled;
  const displayLabel = label || c.label;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: c.bg, color: c.color, fontSize: 12, fontWeight: 700 }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: c.color }} />
      {displayLabel}
    </span>
  );
};

// ── OperationalStatusBadge ─────────────────────────────────────────────────────
const OperationalStatusBadge = ({ status, label, size = 'md' }) => {
  const cfg = {
    active:    { label: 'Ativo',      bg: '#e8f5ee', color: '#0f7a3e' },
    suspended: { label: 'Suspenso',   bg: '#fef3c7', color: '#b45309' },
    archived:  { label: 'Arquivado',  bg: '#f4f5f7', color: '#6b7691' },
  };
  const c = cfg[status] || cfg.active;
  const displayLabel = label || c.label;
  const pad = size === 'sm' ? '2px 8px' : '3px 10px';
  const fs = size === 'sm' ? 11 : 12;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: pad, borderRadius: 20, background: c.bg, color: c.color, fontSize: fs, fontWeight: 700, whiteSpace: 'nowrap' }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: c.color }} />
      {displayLabel}
    </span>
  );
};

// ── CommercialStatusReasonModal ───────────────────────────────────────────────
const CommercialStatusReasonModal = ({ open, tenantName, currentStatus, onClose, onConfirm }) => {
  const options = [
    { value: 'trial', label: 'Trial' },
    { value: 'active', label: 'Ativo' },
    { value: 'past_due', label: 'Inadimplente' },
    { value: 'suspended', label: 'Suspenso' },
    { value: 'cancelled', label: 'Cancelado' },
  ];
  const [newStatus, setNewStatus] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const dialogRef = React.useRef(null);
  const statusSelectRef = React.useRef(null);
  const triggerRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) {
      setNewStatus('');
      setReason('');
      setNotes('');
      setSaving(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape' && !saving) {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )).filter((el) => {
        const style = window.getComputedStyle(el);
        return style.visibility !== 'hidden' && style.display !== 'none';
      });
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    const timer = setTimeout(() => {
      if (statusSelectRef.current && typeof statusSelectRef.current.focus === 'function') {
        statusSelectRef.current.focus();
      }
    }, 0);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, saving, onClose]);

  React.useEffect(() => {
    if (open) return undefined;
    const timer = setTimeout(() => {
      if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
        triggerRef.current.focus();
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [open]);

  if (!open) return null;

  const currentCommercial = typeof uiStatusToCommercial !== 'undefined'
    ? uiStatusToCommercial(currentStatus)
    : currentStatus;
  const isCritical = ['past_due', 'suspended', 'cancelled'].includes(newStatus);
  const canSubmit = newStatus && reason.trim().length >= 5 && !saving && newStatus !== currentCommercial;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await onConfirm({ status: newStatus, reason, notes });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="billing-commercial-modal-backdrop"
      onClick={() => { if (!saving) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-commercial-modal-title"
        aria-describedby="billing-commercial-modal-desc"
        className="billing-commercial-modal"
        onClick={e => e.stopPropagation()}
        style={{ width: 'min(520px, 100%)', maxHeight: '100dvh', background: 'var(--bg-elevated)', borderRadius: 16, boxShadow: 'var(--shadow-lg)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div id="billing-commercial-modal-title" style={{ fontWeight: 700, fontSize: 15 }}>Alterar status comercial</div>
          <div id="billing-commercial-modal-desc" style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>{tenantName}</div>
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', flex: 1 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <BillingStatusBadge status={currentCommercial} />
            <Icon name="arrowRight" size={16} style={{ color: 'var(--text-muted)' }} aria-hidden="true" />
            <label htmlFor="billing-commercial-modal-status" className="billing-sr-only">Novo status comercial</label>
            <select
              id="billing-commercial-modal-status"
              ref={statusSelectRef}
              className="select"
              value={newStatus}
              onChange={e => setNewStatus(e.target.value)}
              style={{ flex: '1 1 180px' }}
            >
              <option value="">Selecione o novo status</option>
              {options.filter(o => o.value !== currentCommercial).map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          {isCritical && (
            <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--warning)', lineHeight: 1.5 }}>
              Nesta fase o status é apenas comercial — não bloqueia automaticamente o acesso da loja.
            </div>
          )}
          <div>
            <label htmlFor="billing-commercial-modal-reason" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Motivo *</label>
            <textarea
              id="billing-commercial-modal-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              placeholder="Mínimo 5 caracteres"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>
          <div>
            <label htmlFor="billing-commercial-modal-notes" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Observações internas</label>
            <textarea
              id="billing-commercial-modal-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>
        </div>
        <div style={{ padding: '12px 24px', background: 'var(--bg-subtle)', display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0, flexWrap: 'wrap' }}>
          <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button
            type="button"
            className={'btn ' + (isCritical ? 'danger' : 'primary')}
            onClick={handleConfirm}
            disabled={!canSubmit}
            aria-busy={saving || undefined}
          >
            {saving ? 'Salvando…' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── OperationalStatusReasonModal ─────────────────────────────────────────────
const OperationalStatusReasonModal = ({ open, action, tenantName, onClose, onConfirm }) => {
  const [reason, setReason] = React.useState('');
  const [understood, setUnderstood] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setReason('');
      setUnderstood(false);
      setSaving(false);
    }
  }, [open]);

  if (!open) return null;

  const isArchive = action === 'archive';
  const title = isArchive ? 'Arquivar loja' : 'Reativar loja';
  const canSubmit = reason.trim().length >= 5 && understood && !saving;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await onConfirm({ reason });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 520, background: 'var(--bg-elevated)', borderRadius: 16, boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{title}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>{tenantName}</div>
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: isArchive ? 'var(--warning-bg)' : 'var(--bg-subtle)', border: `1px solid ${isArchive ? 'var(--warning)' : 'var(--border)'}`, borderRadius: 8, padding: '10px 14px', fontSize: 12, color: isArchive ? 'var(--warning)' : 'var(--text-secondary)', lineHeight: 1.5 }}>
            {isArchive
              ? <>Arquivar <strong>bloqueia a operação</strong> da loja. Storefront, checkout, APIs e GraphQL do tenant ficam indisponíveis, e o dashboard da loja é bloqueado. O acesso pelo Super Admin continua disponível. Dados, schema, domínio e plano são preservados, e o status comercial não é alterado. A loja poderá ser reativada posteriormente.</>
              : <>Reativar <strong>restaura o acesso operacional</strong> da loja, incluindo storefront, checkout, APIs, GraphQL e dashboard. Os dados existentes são preservados e não precisam ser recriados. O status comercial permanece inalterado.</>}
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Motivo *</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="Mínimo 5 caracteres"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', background: 'var(--bg-subtle)', padding: '10px 12px', borderRadius: 8 }}>
            <input type="checkbox" checked={understood} onChange={e => setUnderstood(e.target.checked)} style={{ width: 14, height: 14, marginTop: 2, accentColor: 'var(--primary)' }} />
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {isArchive
                ? 'Entendo que a loja ficará operacionalmente indisponível para clientes e lojistas e que esta ação será registrada na auditoria.'
                : 'Entendo que o acesso operacional será restaurado e que esta ação será registrada na auditoria.'}
            </div>
          </label>
        </div>
        <div style={{ padding: '12px 24px', background: 'var(--bg-subtle)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className={'btn ' + (isArchive ? 'danger' : 'primary')} onClick={handleConfirm} disabled={!canSubmit}>
            {saving ? 'Salvando…' : (isArchive ? 'Arquivar loja' : 'Reativar loja')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── InvoiceStatusBadge ─────────────────────────────────────────────────────────
const InvoiceStatusBadge = ({ status }) => {
  const cfg = {
    draft:          { label: 'Rascunho',   bg: '#f4f5f7', color: '#6b7691' },
    open:           { label: 'Em aberto',  bg: '#dde7ff', color: '#1d4ed8' },
    paid:           { label: 'Pago',       bg: '#e8f5ee', color: '#0f7a3e' },
    overdue:        { label: 'Vencido',    bg: '#fef3c7', color: '#b45309' },
    void:           { label: 'Cancelado',  bg: '#f4f5f7', color: '#6b7691' },
    uncollectible:  { label: 'Incobrável', bg: '#fdecf0', color: '#EC2A58' },
    refunded:       { label: 'Reembolsado',bg: '#ede9fe', color: '#7c3aed' },
  };
  const c = cfg[status] || cfg.open;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: c.bg, color: c.color, fontSize: 12, fontWeight: 700 }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: c.color }} />
      {c.label}
    </span>
  );
};

// ── PlanBadge ──────────────────────────────────────────────────────────────────
const PlanBadge = ({ plan }) => {
  if (plan == null || plan === '') {
    return (
      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>
    );
  }
  const planId = typeof plan === 'object' ? (plan.id || plan.code || 'basic') : plan;
  const colors = {
    pro:         { bg: '#1B3D60', color: '#fff'     },
    basic:       { bg: '#f4f5f7', color: '#4b5563'  },
    enterprise:  { bg: '#7c3aed', color: '#fff'     },
    // Legacy aliases — display-only, not selectable in forms
    scale:       { bg: '#1B3D60', color: '#fff'     }, // → pro
    growth:      { bg: '#0f7a3e', color: '#fff'     }, // → pro
    starter:     { bg: '#f4f5f7', color: '#4b5563'  }, // → basic
  };
  const c = colors[planId] || colors.basic;
  const safeId = String(planId || 'basic');
  const name = typeof plan === 'object'
    ? (plan.name || safeId)
    : (safeId.charAt(0).toUpperCase() + safeId.slice(1));
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 20, background: c.bg, color: c.color, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {name}
    </span>
  );
};

// ── HealthScoreBadge ───────────────────────────────────────────────────────────
const HealthScoreBadge = ({ score }) => {
  const color = score >= 80 ? '#0f7a3e' : score >= 60 ? '#b45309' : '#EC2A58';
  const bg    = score >= 80 ? '#e8f5ee' : score >= 60 ? '#fef3c7' : '#fdecf0';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, background: bg, color, fontSize: 12, fontWeight: 700 }}>
      {score}
      <span style={{ fontSize: 10, opacity: 0.75 }}>/ 100</span>
    </span>
  );
};

// ── TenantStatusSelect ─────────────────────────────────────────────────────────
const TenantStatusSelect = ({ value, onChange, exclude = [] }) => {
  const options = [
    { value: 'active',              label: 'Ativo' },
    { value: 'trial',               label: 'Trial' },
    { value: 'overdue',             label: 'Inadimplente' },
    { value: 'suspended',           label: 'Suspenso' },
    { value: 'provisioning',        label: 'Provisionando' },
    { value: 'provisioning_failed', label: 'Falha de provisionamento' },
    { value: 'cancelled',           label: 'Cancelado' },
  ].filter(o => !exclude.includes(o.value));

  return (
    <select value={value} onChange={e => onChange(e.target.value)} className="select" style={{ width: '100%' }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
};

// ── TenantStatusTimeline ───────────────────────────────────────────────────────
const TenantStatusTimeline = ({ history = [], currentStatus }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return dateStr; }
  };

  const sourceLabel = { billing: 'Billing', sistema: 'Sistema', manual: 'Manual', suporte: 'Suporte', provisioning_system: 'Provisionamento', billing_system: 'Billing automático' };

  const allEvents = [
    ...history.map(h => ({
      id: h.id,
      label: h.new_status,
      reason: h.reason,
      by: h.changed_by,
      source: h.source,
      date: h.created_at,
      kind: 'change',
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  if (allEvents.length === 0) {
    return (
      <div style={{ padding: '20px 0', color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}>
        Nenhum histórico de status disponível.
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', paddingLeft: 28 }}>
      {allEvents.map((ev, idx) => {
        const c = getStatusCfg(ev.label);
        return (
          <div key={ev.id} style={{ position: 'relative', paddingBottom: idx < allEvents.length - 1 ? 20 : 0, display: 'flex', gap: 12 }}>
            {idx < allEvents.length - 1 && (
              <div style={{ position: 'absolute', left: -17, top: 22, bottom: 0, width: 2, background: 'var(--border)' }} />
            )}
            <div style={{ position: 'absolute', left: -22, top: 6, width: 10, height: 10, borderRadius: '50%', background: c.color, border: '2px solid var(--bg-elevated)', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}>
                <TenantStatusBadge status={ev.label} size="sm" />
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatDate(ev.date)}</span>
              </div>
              {ev.reason && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 2 }}>{ev.reason}</div>}
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                por <strong>{typeof ev.by === 'object' ? (ev.by?.email || '—') : (ev.by || '—')}</strong>
                {ev.source ? <> · via {sourceLabel[ev.source] || ev.source}</> : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── TenantStatusReasonModal ────────────────────────────────────────────────────
const TenantStatusReasonModal = ({ open, store, currentStatus, onClose, onConfirm }) => {
  const [newStatus,     setNewStatus]     = React.useState('');
  const [reason,        setReason]        = React.useState('');
  const [notifyOwner,   setNotifyOwner]   = React.useState(true);
  const [understood,    setUnderstood]    = React.useState(false);
  const [saving,        setSaving]        = React.useState(false);

  React.useEffect(() => {
    if (!open) { setNewStatus(''); setReason(''); setNotifyOwner(true); setUnderstood(false); setSaving(false); }
  }, [open]);

  if (!open) return null;

  const isSuspend    = newStatus === 'suspended' || newStatus === 'suspenso';
  const isOverdue    = newStatus === 'overdue'   || newStatus === 'inadimplente';
  const isCritical   = isSuspend || isOverdue || newStatus === 'cancelled' || newStatus === 'cancelado';
  const canSubmit    = newStatus && reason.trim().length >= 5 && (!isCritical || understood) && !saving;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await onConfirm({ status: newStatus, reason, notify_owner: notifyOwner });
    } finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 520, background: 'var(--bg-elevated)', borderRadius: 16, boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--warning-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name="alert" size={20} style={{ color: 'var(--warning)' }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Alterar status do tenant</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{store?.name}</div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Status atual */}
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Status atual</div>
              <div style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}>
                <TenantStatusBadge status={currentStatus} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', paddingTop: 24 }}>
              <Icon name="arrowRight" size={18} style={{ color: 'var(--text-muted)' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Novo status <span style={{ color: 'var(--danger)' }}>*</span></div>
              <TenantStatusSelect value={newStatus} onChange={setNewStatus} exclude={[currentStatus]} />
            </div>
          </div>

          {/* Warnings contextuais */}
          {isSuspend && (
            <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--warning)', lineHeight: 1.5 }}>
              <strong>Atenção:</strong> Altera apenas o <strong>status comercial</strong>. Nesta fase <strong>não bloqueia</strong> automaticamente o acesso da loja. Para bloquear a operação, use Arquivar loja.
            </div>
          )}
          {isOverdue && (
            <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--warning)', lineHeight: 1.5 }}>
              <strong>Atenção:</strong> Marcar como inadimplente pode iniciar a régua de cobrança automática e aplicar restrições operacionais à loja.
            </div>
          )}

          {/* Motivo */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Motivo <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Descreva o motivo da alteração de status (mínimo 5 caracteres)..." rows={3}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.5 }} />
          </div>

          {/* Checkboxes */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={notifyOwner} onChange={e => setNotifyOwner(e.target.checked)} style={{ width: 14, height: 14, marginTop: 2, accentColor: 'var(--primary)' }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Notificar o owner por e-mail</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{store?.ownerEmail || 'owner@loja.com.br'}</div>
              </div>
            </label>
            {isCritical && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', background: 'var(--bg-subtle)', padding: '10px 12px', borderRadius: 8 }}>
                <input type="checkbox" checked={understood} onChange={e => setUnderstood(e.target.checked)} style={{ width: 14, height: 14, marginTop: 2, accentColor: 'var(--danger)' }} />
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  Entendo o impacto desta ação. Esta alteração será <strong>registrada na auditoria</strong> com data, hora, motivo e ator responsável.
                </div>
              </label>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ padding: '12px 24px', background: 'var(--bg-subtle)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button
            className={'btn ' + (isCritical ? 'danger' : 'primary')}
            onClick={handleConfirm}
            disabled={!canSubmit}
          >
            {saving
              ? <><Icon name="refresh" size={13} style={{ animation: 'spin 1s linear infinite' }} />Salvando…</>
              : `Confirmar alteração`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── NotifSeverityBadge ─────────────────────────────────────────────────────────
const NotifSeverityBadge = ({ severity }) => {
  const cfg = {
    info:     { label: 'Info',       bg: '#dde7ff', color: '#1d4ed8' },
    warning:  { label: 'Alerta',     bg: '#fef3c7', color: '#b45309' },
    error:    { label: 'Erro',       bg: '#fdecf0', color: '#EC2A58' },
    critical: { label: 'Crítico',    bg: '#fdecf0', color: '#EC2A58' },
    security: { label: 'Segurança',  bg: '#ede9fe', color: '#7c3aed' }, // mock-only legacy
  };
  const c = cfg[severity] || cfg.info;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 20, background: c.bg, color: c.color, fontSize: 11, fontWeight: 700 }}>
      {c.label}
    </span>
  );
};

// ── SuperAdminRoleBadge ────────────────────────────────────────────────────────
const SuperAdminRoleBadge = ({ role }) => {
  const colors = {
    owner:       { bg: '#fdecf0', color: '#EC2A58' },
    admin:       { bg: '#dde7ff', color: '#1d4ed8' },
    support_l1:  { bg: '#f4f5f7', color: '#4b5563' },
    support_l2:  { bg: '#fef3c7', color: '#b45309' },
    finance:     { bg: '#e8f5ee', color: '#0f7a3e' },
    product:     { bg: '#ede9fe', color: '#7c3aed' },
    engineering: { bg: '#f4f5f7', color: '#1B3D60' },
    auditor:     { bg: '#f4f5f7', color: '#4b5563' },
  };
  const code = typeof role === 'object' ? role.code : role;
  const name = typeof role === 'object' ? role.name : (code.charAt(0).toUpperCase() + code.slice(1));
  const c = colors[code] || colors.auditor;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: c.bg, color: c.color, fontSize: 12, fontWeight: 600 }}>
      {name}
    </span>
  );
};

export {
  BillingStatusBadge,
  CommercialStatusReasonModal,
  HealthScoreBadge,
  InvoiceStatusBadge,
  NotifSeverityBadge,
  OperationalStatusBadge,
  OperationalStatusReasonModal,
  PlanBadge,
  SuperAdminRoleBadge,
  TenantStatusBadge,
  TenantStatusReasonModal,
  TenantStatusSelect,
  TenantStatusTimeline,
  getStatusCfg,
};
